import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getRedirectGroups,
    deleteRedirectGroup,
    syncRedirectGroup,
    getSyncLogs,
    buildPublicRedirectUrl,
    buildPublicRedirectUrlWithLang,
} from '@/services/api/redirect-groups';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    Plus,
    Pencil,
    Trash2,
    RefreshCw,
    Copy,
    History,
    ExternalLink,
    Globe,
    Link2,
    AlertCircle,
    CheckCircle2,
    Clock,
    TrendingUp,
    DollarSign,
    Target,
    Eye,
    MousePointerClick,
    Zap,
    Trophy,
    Award,
    Medal,
    Search,
} from 'lucide-react';
import RedirectGroupForm from './RedirectGroupForm';
import type { RedirectGroup } from '@/types/redirect-group';

export default function RedirectGroups() {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [formOpen, setFormOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<RedirectGroup | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<RedirectGroup | null>(null);
    const [logsGroupId, setLogsGroupId] = useState<number | null>(null);

    const { data: groups, isLoading, error, refetch } = useQuery({
        queryKey: ['redirect-groups'],
        queryFn: () => getRedirectGroups(false),
        retry: 1,
        retryDelay: 800,
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) => deleteRedirectGroup(id),
        onSuccess: () => {
            toast({ title: 'Grupo deletado!' });
            queryClient.invalidateQueries({ queryKey: ['redirect-groups'] });
            setDeleteConfirm(null);
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao deletar',
                description: err.response?.data?.detail || err.message,
            });
        },
    });

    const syncMutation = useMutation({
        mutationFn: (id: number) => syncRedirectGroup(id, false),
        onSuccess: (result) => {
            toast({
                title: 'Sync executado!',
                description: `${result.total_urls || 0} URLs no pool (${result.duration_ms || 0}ms)`,
            });
            queryClient.invalidateQueries({ queryKey: ['redirect-groups'] });
        },
        onError: (err: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro no sync',
                description: err.response?.data?.detail || err.message,
            });
        },
    });

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: 'Copiado!', description: text });
    };

    const openCreate = () => {
        setEditingGroup(null);
        setFormOpen(true);
    };

    const openEdit = (group: RedirectGroup) => {
        setEditingGroup(group);
        setFormOpen(true);
    };

    const renderSyncStatus = (group: RedirectGroup) => {
        if (!group.last_sync_at) {
            return (
                <Badge variant="outline">
                    <Clock className="mr-1 h-3 w-3" />
                    Nunca
                </Badge>
            );
        }

        const status = group.last_sync_status;
        if (status === 'success') {
            return (
                <Badge variant="success">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    OK
                </Badge>
            );
        }
        if (status === 'partial') {
            return (
                <Badge variant="secondary">
                    <AlertCircle className="mr-1 h-3 w-3" />
                    Parcial
                </Badge>
            );
        }
        return (
            <Badge variant="destructive">
                <AlertCircle className="mr-1 h-3 w-3" />
                Falhou
            </Badge>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Redirect Groups</h1>
                    <p className="text-xs md:text-sm text-gray-600">
                        Gerencie grupos de redirect inteligentes baseados em RPS, eCPM ou URLs manuais.
                    </p>
                </div>
                <Button onClick={openCreate}>
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Grupo
                </Button>
            </div>

            {/* Loading Skeleton */}
            {isLoading && (
                <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </div>
                    <div className="divide-y divide-gray-100">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="px-6 py-5 flex items-center gap-4">
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
                                    <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                                </div>
                                <div className="h-6 w-16 bg-gray-200 rounded-full animate-pulse" />
                                <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                                <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
                                <div className="h-6 w-20 bg-gray-200 rounded-full animate-pulse" />
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4].map((j) => (
                                        <div
                                            key={j}
                                            className="h-8 w-8 bg-gray-100 rounded animate-pulse"
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Error */}
            {!isLoading && error && (
                <div className="rounded-xl bg-white border border-red-200 shadow-sm overflow-hidden">
                    <div className="p-8 flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                            Erro ao carregar grupos
                        </h3>
                        <p className="text-sm text-gray-600 mb-2 max-w-md">
                            Nao foi possivel conectar ao servico de redirect. Verifique se o
                            ms-redirect esta online e acessivel.
                        </p>
                        <code className="text-[11px] text-red-700 bg-red-50 px-3 py-1.5 rounded mb-4 max-w-full truncate">
                            {(error as any)?.message || 'Erro desconhecido'}
                        </code>
                        <Button variant="outline" onClick={() => refetch()}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Tentar novamente
                        </Button>
                    </div>
                </div>
            )}

            {/* Empty */}
            {!isLoading && !error && groups && groups.length === 0 && (
                <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-12 flex flex-col items-center text-center">
                        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4">
                            <Link2 className="h-8 w-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                            Nenhum grupo criado ainda
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 max-w-md">
                            Comece criando seu primeiro grupo de redirect inteligente para
                            otimizar seu trafego.
                        </p>
                        <Button onClick={openCreate}>
                            <Plus className="mr-2 h-4 w-4" />
                            Criar primeiro grupo
                        </Button>
                    </div>
                </div>
            )}

            {/* Table */}
            {!isLoading && !error && groups && groups.length > 0 && (
                <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Nome
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Items
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    URL Publica
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                    Ultimo Sync
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                                    Acoes
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {groups.map((group) => {
                                const publicUrl = buildPublicRedirectUrl(group.hash_redirect);
                                const domainCount = (group.items || []).filter((i) => i.item_type === 'domain').length;
                                const manualCount = (group.items || []).filter((i) => i.item_type === 'manual_url').length;

                                return (
                                    <tr key={group.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{group.name}</p>
                                                <p className="text-xs text-gray-500">{group.slug}</p>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                <Badge variant={group.redirect_type === 'smart' ? 'default' : 'secondary'}>
                                                    {group.redirect_type === 'smart' ? 'Smart' : 'Manual'}
                                                </Badge>
                                                {group.redirect_type === 'smart' && group.sync_mode && (
                                                    <p className="text-xs text-gray-500 uppercase">
                                                        {group.sync_mode} · {group.period.replace('_', ' ')}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col gap-1 text-xs">
                                                {domainCount > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <Globe className="h-3 w-3" />
                                                        {domainCount} dominio(s)
                                                    </span>
                                                )}
                                                {manualCount > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <Link2 className="h-3 w-3" />
                                                        {manualCount} URL(s) manual
                                                    </span>
                                                )}
                                                {domainCount === 0 && manualCount === 0 && (
                                                    <span className="text-gray-400">Nenhum</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-2">
                                                {/* URL com lang placeholder */}
                                                <div className="flex items-center gap-1">
                                                    <code className="text-xs bg-blue-50 border border-blue-200 px-2 py-1 rounded font-mono max-w-[220px] truncate">
                                                        /<span className="text-blue-600 font-semibold">{'{lang}'}</span>/r/{group.hash_redirect}
                                                    </code>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            copyToClipboard(buildPublicRedirectUrlWithLang(group.hash_redirect))
                                                        }
                                                        title="Copiar URL com {lang}"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                    </Button>
                                                </div>

                                                {/* URL simples sem lang */}
                                                <div className="flex items-center gap-1">
                                                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono max-w-[220px] truncate">
                                                        /r/{group.hash_redirect}
                                                    </code>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => copyToClipboard(publicUrl)}
                                                        title="Copiar URL simples"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                    </Button>
                                                    <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                                                        <Button variant="ghost" size="sm" title="Abrir">
                                                            <ExternalLink className="h-3 w-3" />
                                                        </Button>
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge variant={group.is_active ? 'success' : 'destructive'}>
                                                {group.is_active ? 'Ativo' : 'Inativo'}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="space-y-1">
                                                {renderSyncStatus(group)}
                                                {group.last_sync_at && (
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(group.last_sync_at).toLocaleString('pt-BR')}
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => syncMutation.mutate(group.id)}
                                                    disabled={syncMutation.isPending}
                                                    title="Forcar sync"
                                                >
                                                    <RefreshCw
                                                        className={`h-4 w-4 ${
                                                            syncMutation.isPending && syncMutation.variables === group.id
                                                                ? 'animate-spin'
                                                                : ''
                                                        }`}
                                                    />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setLogsGroupId(group.id)}
                                                    title="Ver logs"
                                                >
                                                    <History className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEdit(group)}
                                                    title="Editar"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setDeleteConfirm(group)}
                                                    title="Deletar"
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {/* Form Dialog (Create/Edit) */}
            <RedirectGroupForm
                open={formOpen}
                onOpenChange={setFormOpen}
                group={editingGroup}
            />

            {/* Delete Confirm */}
            <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Deletar grupo</DialogTitle>
                        <DialogDescription>
                            Tem certeza que deseja deletar o grupo <strong>{deleteConfirm?.name}</strong>?
                            Esta acao nao pode ser desfeita e a URL publica deixara de funcionar.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex gap-3 pt-4">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setDeleteConfirm(null)}
                            disabled={deleteMutation.isPending}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
                            disabled={deleteMutation.isPending}
                        >
                            {deleteMutation.isPending ? 'Deletando...' : 'Deletar'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Sync Logs Dialog */}
            <SyncLogsDialog groupId={logsGroupId} onClose={() => setLogsGroupId(null)} />
        </div>
    );
}

// ----------------------------------------------------------------
// Sync Logs Dialog
// ----------------------------------------------------------------

interface PoolEntry {
    url: string;
    source_type: string;
    source_domain: string | null;
    post_id: string | null;
    weight: number;
    metric: number | null;
    rps: number | null;
    ecpm: number | null;
    revenue: number | null;
    pageviews: number | null;
    impressions: number | null;
}

function formatNumber(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined) return '-';
    if (value === 0) return '0';
    return value.toLocaleString('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function formatInt(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-BR');
}

function formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    });
}

function formatRelativeTime(date: string): string {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `${diffMin}m atras`;
    if (diffHour < 24) return `${diffHour}h atras`;
    if (diffDay < 7) return `${diffDay}d atras`;
    return then.toLocaleDateString('pt-BR');
}

// Cores deterministicas por dominio
const DOMAIN_COLORS = [
    'bg-purple-500',
    'bg-blue-500',
    'bg-emerald-500',
    'bg-orange-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-amber-500',
    'bg-indigo-500',
];

function getDomainColor(domain: string | null): string {
    if (!domain) return 'bg-gray-400';
    let hash = 0;
    for (let i = 0; i < domain.length; i++) hash = (hash * 31 + domain.charCodeAt(i)) | 0;
    return DOMAIN_COLORS[Math.abs(hash) % DOMAIN_COLORS.length];
}

function getRankIcon(rank: number) {
    if (rank === 1) return <Trophy className="h-4 w-4 text-yellow-500" />;
    if (rank === 2) return <Award className="h-4 w-4 text-gray-400" />;
    if (rank === 3) return <Medal className="h-4 w-4 text-amber-600" />;
    return null;
}

function SyncLogsDialog({ groupId, onClose }: { groupId: number | null; onClose: () => void }) {
    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
    const [entrySearch, setEntrySearch] = useState('');

    const { data: logs, isLoading } = useQuery({
        queryKey: ['redirect-group-sync-logs', groupId],
        queryFn: () => (groupId ? getSyncLogs(groupId, 50) : Promise.resolve([])),
        enabled: !!groupId,
    });

    // Reset state quando fecha
    const handleClose = () => {
        setSelectedLogId(null);
        setEntrySearch('');
        onClose();
    };

    const selectedLog = logs?.find((l) => l.id === selectedLogId);
    const entries: PoolEntry[] = (selectedLog?.details?.entries || []) as PoolEntry[];
    const totalWeight = (selectedLog?.details?.total_weight as number) || 0;

    const filteredEntries = entrySearch.trim()
        ? entries.filter(
              (e) =>
                  e.url?.toLowerCase().includes(entrySearch.toLowerCase()) ||
                  (e.post_id || '').includes(entrySearch) ||
                  (e.source_domain || '').toLowerCase().includes(entrySearch.toLowerCase())
          )
        : entries;

    return (
        <Dialog open={!!groupId} onOpenChange={(o) => !o && handleClose()}>
            <DialogContent className="max-w-7xl max-h-[92vh] overflow-hidden p-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-slate-50 to-white">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                        <History className="h-5 w-5 text-blue-600" />
                        Historico de Sync
                    </DialogTitle>
                    <DialogDescription>
                        Selecione uma execucao a esquerda para visualizar as URLs e metricas.
                    </DialogDescription>
                </DialogHeader>

                {isLoading && (
                    <div className="flex items-center justify-center py-12">
                        <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
                    </div>
                )}

                {logs && logs.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <History className="h-12 w-12 mb-2 opacity-30" />
                        <p>Nenhum log encontrado</p>
                    </div>
                )}

                {logs && logs.length > 0 && (
                    <div className="grid grid-cols-12 gap-0 max-h-[78vh]">
                        {/* COLUNA ESQUERDA: lista de syncs */}
                        <div className="col-span-3 border-r bg-slate-50/50 overflow-y-auto max-h-[78vh]">
                            <div className="sticky top-0 px-4 py-3 bg-slate-50/95 backdrop-blur border-b z-10">
                                <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                    Execucoes ({logs.length})
                                </h3>
                            </div>
                            <div className="p-2 space-y-1">
                                {logs.map((log) => {
                                    const isSelected = log.id === selectedLogId;
                                    const statusColor =
                                        log.status === 'success'
                                            ? 'bg-emerald-500'
                                            : log.status === 'partial'
                                            ? 'bg-amber-500'
                                            : 'bg-red-500';
                                    return (
                                        <button
                                            type="button"
                                            key={log.id}
                                            onClick={() => setSelectedLogId(log.id)}
                                            className={`w-full text-left rounded-lg p-3 transition-all ${
                                                isSelected
                                                    ? 'bg-white border border-blue-500 shadow-sm ring-1 ring-blue-200'
                                                    : 'hover:bg-white border border-transparent hover:border-gray-200'
                                            }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <span
                                                    className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${statusColor}`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-baseline justify-between">
                                                        <span className="text-sm font-bold text-gray-900">
                                                            {log.total_urls}
                                                            <span className="text-xs font-normal text-gray-500 ml-1">URLs</span>
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            {log.duration_ms}ms
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-gray-500 mt-0.5">
                                                        {log.total_sites_processed} sites · {formatRelativeTime(log.created_at)}
                                                    </div>
                                                    {log.error_message && (
                                                        <div className="mt-1 text-[10px] text-red-600 truncate flex items-center gap-1">
                                                            <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                                            <span className="truncate">{log.error_message}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* COLUNA DIREITA: detalhes do sync selecionado */}
                        <div className="col-span-9 overflow-y-auto max-h-[78vh]">
                            {!selectedLog && (
                                <div className="flex flex-col items-center justify-center h-full text-gray-300 p-12">
                                    <Eye className="h-16 w-16 mb-3 opacity-40" />
                                    <p className="text-sm">Selecione uma execucao para ver os detalhes</p>
                                </div>
                            )}

                            {selectedLog && (
                                <div className="p-6 space-y-6">
                                    {/* Cards de resumo - gradientes */}
                                    <div className="grid grid-cols-4 gap-3">
                                        <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <Target className="h-4 w-4 opacity-80" />
                                                <span className="text-[10px] uppercase tracking-wider opacity-80">Pool</span>
                                            </div>
                                            <div className="text-2xl font-bold">{selectedLog.total_urls}</div>
                                            <div className="text-[11px] opacity-80">URLs disponiveis</div>
                                        </div>

                                        <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 text-white shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <Zap className="h-4 w-4 opacity-80" />
                                                <span className="text-[10px] uppercase tracking-wider opacity-80">Peso</span>
                                            </div>
                                            <div className="text-2xl font-bold">{formatNumber(totalWeight, 2)}</div>
                                            <div className="text-[11px] opacity-80">Total weight</div>
                                        </div>

                                        <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-4 text-white shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <Globe className="h-4 w-4 opacity-80" />
                                                <span className="text-[10px] uppercase tracking-wider opacity-80">Sites</span>
                                            </div>
                                            <div className="text-2xl font-bold">{selectedLog.total_sites_processed}</div>
                                            <div className="text-[11px] opacity-80">Processados</div>
                                        </div>

                                        <div className="rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 p-4 text-white shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <Clock className="h-4 w-4 opacity-80" />
                                                <span className="text-[10px] uppercase tracking-wider opacity-80">Tempo</span>
                                            </div>
                                            <div className="text-2xl font-bold">
                                                {selectedLog.duration_ms < 1000
                                                    ? `${selectedLog.duration_ms}ms`
                                                    : `${(selectedLog.duration_ms / 1000).toFixed(1)}s`}
                                            </div>
                                            <div className="text-[11px] opacity-80">Duracao</div>
                                        </div>
                                    </div>

                                    {/* Per-site stats - cards bonitos */}
                                    {selectedLog.details?.per_site && (
                                        <div>
                                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                                                <Globe className="h-3 w-3" />
                                                Por site
                                            </h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(selectedLog.details.per_site as Record<string, any>).map(
                                                    ([dom, st]) => {
                                                        const pct = st.candidates > 0 ? (st.selected / st.candidates) * 100 : 0;
                                                        return (
                                                            <div
                                                                key={dom}
                                                                className="rounded-lg border bg-white p-3 hover:shadow-sm transition"
                                                            >
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span
                                                                        className={`h-2.5 w-2.5 rounded-full ${getDomainColor(dom)}`}
                                                                    />
                                                                    <span className="text-sm font-semibold text-gray-900 truncate flex-1">
                                                                        {dom}
                                                                    </span>
                                                                    <Badge variant="outline" className="text-[10px]">
                                                                        weight {st.weight}
                                                                    </Badge>
                                                                </div>
                                                                <div className="flex items-center justify-between text-[11px] text-gray-600 mb-1.5">
                                                                    <span>
                                                                        <span className="font-bold text-emerald-600">
                                                                            {st.selected}
                                                                        </span>{' '}
                                                                        / {st.candidates} selecionados
                                                                    </span>
                                                                    <span className="text-gray-400">{pct.toFixed(0)}%</span>
                                                                </div>
                                                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                                    <div
                                                                        className={`h-full ${getDomainColor(dom)} transition-all`}
                                                                        style={{ width: `${pct}%` }}
                                                                    />
                                                                </div>
                                                                {st.error && (
                                                                    <div className="mt-2 text-[10px] text-red-600 flex items-center gap-1">
                                                                        <AlertCircle className="h-3 w-3" />
                                                                        {st.error}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* URLs do pool */}
                                    <div>
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-2">
                                                <TrendingUp className="h-3 w-3" />
                                                URLs no pool ({filteredEntries.length})
                                            </h4>
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
                                                <Input
                                                    value={entrySearch}
                                                    onChange={(e) => setEntrySearch(e.target.value)}
                                                    placeholder="Filtrar URL, post ou dominio..."
                                                    className="h-8 text-xs w-64 pl-7"
                                                />
                                            </div>
                                        </div>

                                        {entries.length === 0 ? (
                                            <div className="text-center py-12 text-gray-400">
                                                <Eye className="h-10 w-10 mx-auto mb-2 opacity-40" />
                                                <p className="text-sm">Nenhuma URL nesse sync</p>
                                            </div>
                                        ) : (
                                            <div className="rounded-xl border overflow-hidden">
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full text-xs">
                                                        <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                                                            <tr>
                                                                <th className="px-3 py-2.5 text-left font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    #
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    URL
                                                                </th>
                                                                <th className="px-3 py-2.5 text-right font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <TrendingUp className="h-3 w-3" />
                                                                        RPS
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 text-right font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    eCPM
                                                                </th>
                                                                <th className="px-3 py-2.5 text-right font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <DollarSign className="h-3 w-3" />
                                                                        Revenue
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 text-right font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <Eye className="h-3 w-3" />
                                                                        PV
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 text-right font-bold text-gray-600 uppercase tracking-wider text-[10px]">
                                                                    <div className="flex items-center justify-end gap-1">
                                                                        <MousePointerClick className="h-3 w-3" />
                                                                        Impr
                                                                    </div>
                                                                </th>
                                                                <th className="px-3 py-2.5 text-left font-bold text-gray-600 uppercase tracking-wider text-[10px] w-44">
                                                                    Chance
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100 bg-white">
                                                            {filteredEntries.map((entry, idx) => {
                                                                const pct =
                                                                    totalWeight > 0
                                                                        ? (entry.weight / totalWeight) * 100
                                                                        : 0;
                                                                const rank = idx + 1;
                                                                const isTop = rank <= 3 && !entrySearch.trim();
                                                                let urlPath = entry.url;
                                                                let urlHost = '';
                                                                try {
                                                                    const u = new URL(entry.url);
                                                                    urlHost = u.host;
                                                                    urlPath = u.pathname + u.search;
                                                                } catch (_) {
                                                                    // ignore
                                                                }
                                                                return (
                                                                    <tr
                                                                        key={idx}
                                                                        className={`hover:bg-blue-50/40 transition ${
                                                                            isTop ? 'bg-amber-50/20' : ''
                                                                        }`}
                                                                    >
                                                                        <td className="px-3 py-2.5 align-middle">
                                                                            <div className="flex items-center gap-1.5">
                                                                                {getRankIcon(rank)}
                                                                                <span
                                                                                    className={`font-bold ${
                                                                                        rank === 1
                                                                                            ? 'text-yellow-600'
                                                                                            : rank === 2
                                                                                            ? 'text-gray-500'
                                                                                            : rank === 3
                                                                                            ? 'text-amber-700'
                                                                                            : 'text-gray-400'
                                                                                    }`}
                                                                                >
                                                                                    {rank}
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-3 py-2.5">
                                                                            <div className="flex items-start gap-2">
                                                                                <span
                                                                                    className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${getDomainColor(
                                                                                        entry.source_domain || urlHost
                                                                                    )}`}
                                                                                />
                                                                                <div className="min-w-0 flex-1">
                                                                                    <a
                                                                                        href={entry.url}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="text-blue-600 hover:underline font-mono text-[11px] truncate block max-w-[280px]"
                                                                                        title={entry.url}
                                                                                    >
                                                                                        {urlPath}
                                                                                    </a>
                                                                                    <div className="flex items-center gap-2 mt-0.5">
                                                                                        <span className="text-[9px] text-gray-500 truncate max-w-[200px]">
                                                                                            {urlHost}
                                                                                        </span>
                                                                                        {entry.post_id && (
                                                                                            <Badge
                                                                                                variant="outline"
                                                                                                className="text-[9px] py-0 h-4"
                                                                                            >
                                                                                                #{entry.post_id}
                                                                                            </Badge>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-semibold">
                                                                            {formatNumber(entry.rps, 4)}
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-right font-mono text-purple-700 font-semibold">
                                                                            {formatNumber(entry.ecpm, 2)}
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-right font-mono text-gray-700">
                                                                            {formatCurrency(entry.revenue)}
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-right font-mono text-gray-600">
                                                                            {formatInt(entry.pageviews)}
                                                                        </td>
                                                                        <td className="px-3 py-2.5 text-right font-mono text-gray-600">
                                                                            {formatInt(entry.impressions)}
                                                                        </td>
                                                                        <td className="px-3 py-2.5">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden min-w-[80px]">
                                                                                    <div
                                                                                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all"
                                                                                        style={{
                                                                                            width: `${Math.min(100, pct)}%`,
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                                <span className="text-[10px] font-bold text-gray-700 w-12 text-right">
                                                                                    {pct.toFixed(2)}%
                                                                                </span>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Erro */}
                                    {selectedLog.error_message && (
                                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-700 flex items-start gap-2">
                                            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                            <div className="flex-1">
                                                <div className="font-semibold mb-1">Erro durante o sync</div>
                                                <div className="font-mono text-[11px]">{selectedLog.error_message}</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Timestamp completo */}
                                    <div className="text-center text-[10px] text-gray-400 pt-2 border-t">
                                        Executado em {new Date(selectedLog.created_at).toLocaleString('pt-BR')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
